'use client';

import { Footer } from '@/components/public/footer';
import { NavPreview } from '@/components/public/nav-preview';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
import { ArrowRight, Crown, Wine, Flower } from 'lucide-react';
import Image from 'next/image';
import { Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { FaqAccordion } from '@/components/public/faq-accordion';
import { useFaqCount } from '@/lib/faq-hooks';

function FAQAccordion() {
  const t = useTranslations('design.newsAndOffers');
  const faqCount = useFaqCount('design.newsAndOffers.faq');
  const items = Array.from({ length: faqCount }, (_, idx) => idx + 1).map((i) => ({
    question: t(`faq.q${i}`),
    answerParagraphs: [t(`faq.a${i}`)],
  }));

  return (
    <section className="design-section-gap">
      <div className="max-w-4xl mx-auto design-content-pad lg:py-20">
        <div className="text-center mb-16 observe-me slide-up">
          <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
            <span className="text-[#801515]">{t('faqSection.label')}</span>
            <br />
            <span className="text-gray-800 text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
              {t('faqSection.heading')}
            </span>
          </h2>
          <div className="w-16 h-1 bg-[#801515] mx-auto" />
        </div>

        <FaqAccordion items={items} accent="#801515" />
      </div>
    </section>
  );
}

export default function NewsAndOffersPage() {
  const t = useTranslations('design.newsAndOffers');

  useScrollReveal();

  return (
    <main className="min-h-screen bg-white text-gray-900">
      <style jsx global>{`
        @keyframes breatheSlow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        .image-breathe-slow { animation: breatheSlow 10s ease-in-out infinite; }
      `}</style>

      <NavPreview />

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        <Image
          src="/design-images/uploads/1777555000805-dsc03994-1-.jpg"
          alt={t('hero.imageAlt')}
          fill
          className="object-cover image-breathe-slow"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="relative h-full flex flex-col items-center justify-center px-6 text-center">
          <div className="observe-me slide-up w-full max-w-3xl">
            <h1 className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-8 tracking-tight break-words" style={{ textShadow: '2px 2px 30px rgba(0, 0, 0, 0.5)' }}>
              {t('hero.title')}
            </h1>
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="w-8 sm:w-16 h-1 bg-[#801515] shrink-0" />
              <p className="text-lg md:text-2xl text-white/90 font-light uppercase text-center break-words [text-shadow:rgba(0,0,0,1)_1px_1px_3px] md:[text-shadow:rgb(0,0,0)_1.5px_1.5px_4px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>
                {t('hero.subtitle')}
              </p>
              <div className="w-8 sm:w-16 h-1 bg-[#801515] shrink-0" />
            </div>
            <p className="text-lg sm:text-2xl md:text-3xl text-white/85 w-fit max-w-2xl mx-auto font-light [text-shadow:rgba(0,0,0,1)_1px_1px_3px] sm:[text-shadow:rgb(0,0,0)_1.5px_1.5px_4px] md:[text-shadow:rgb(0,0,0)_2px_2px_5px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>
              {t('hero.description')}
            </p>
          </div>
        </div>
      </section>

      {/* New Year's Eve Section - Asymmetric Layout (Content Left / Image Right) */}
      <section className="grid lg:grid-cols-5 min-h-[80vh] design-section-gap rounded-3xl overflow-hidden">
        {/* Content Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#faf5f5] via-[#fef7f0] to-[#faf5f5] order-1">
          <div className="max-w-lg observe-me slide-right">
            <div className="inline-block px-6 py-2 bg-[#801515] text-white text-sm font-semibold rounded-full mb-6 uppercase tracking-wider">
              {t('newYearsEve.badge')}
            </div>

            <h2 className="font-poppins text-2xl sm:text-4xl md:text-5xl xl:text-6xl font-bold mb-4 leading-[1.1]">
              <span className="text-[#801515]">{t('newYearsEve.titleAccent')}</span>
              <br />
              <span className="text-gray-800 text-2xl md:text-3xl xl:text-4xl font-normal mt-3 block">
                {t('newYearsEve.titleSub')}
              </span>
            </h2>

            <div className="w-16 h-1 bg-[#801515] mb-6" />

            <p className="text-lg text-gray-700 leading-relaxed mb-6 font-light italic">
              <span className="font-bold">{t('newYearsEve.descriptionDate')}</span>
              <br />
              {t('newYearsEve.description')}
            </p>

            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-6">
              {t('newYearsEve.footnote')}
            </p>

            <div>
              <Link
                href="/offers/new-years-eve-2027"
                className="group w-full lg:w-auto px-[26px] py-5 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl rounded-full transition-all duration-500 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30"
              >
                {t('newYearsEve.cta')}
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <p className="text-xs text-gray-400 mt-6 text-center lg:text-left">
              {t('newYearsEve.publishDate')}
            </p>
          </div>
        </div>

        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 observe-me slide-left">
          <Image
            src="/design-images/uploads/1786541739440-x6toss-chatgpt-image-11-aug.-2026-09_03_51.jpg"
            alt={t('newYearsEve.imageAlt')}
            fill
            className="object-cover"
            style={{ objectPosition: 'center' }}
          />
        </div>
      </section>

      {/* Autumn Break Section - Asymmetric Layout (Image Left / Content Right) */}
      <section className="grid lg:grid-cols-5 min-h-[80vh] design-section-gap rounded-3xl overflow-hidden">
        {/* Content Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#faf5f5] via-[#fef7f0] to-[#faf5f5] order-2">
          <div className="max-w-lg observe-me slide-right">
            <div className="inline-block px-6 py-2 bg-[#801515] text-white text-sm font-semibold rounded-full mb-6 uppercase tracking-wider">
              {t('autumnBreak.badge')}
            </div>

            <h2 className="font-poppins text-2xl sm:text-4xl md:text-5xl xl:text-6xl font-bold mb-4 leading-[1.1]">
              <span className="text-[#801515]">{t('autumnBreak.titleAccent')}</span>
              <br />
              <span className="text-gray-800 text-2xl md:text-3xl xl:text-4xl font-normal mt-3 block">
                {t('autumnBreak.titleSub')}
              </span>
            </h2>

            <div className="w-16 h-1 bg-[#801515] mb-6" />

            <p className="text-lg text-gray-700 leading-relaxed mb-6 font-light italic">
              <span className="font-bold">{t('autumnBreak.descriptionDate')}</span>
              <br />
              {t('autumnBreak.description')}
            </p>

            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-6">
              {t('autumnBreak.footnote')}
            </p>

            <div>
              <Link
                href="/offers/autumn-2026"
                className="group w-full lg:w-auto px-[26px] py-5 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl rounded-full transition-all duration-500 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30"
              >
                {t('autumnBreak.cta')}
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <p className="text-xs text-gray-400 mt-6 text-center lg:text-left">
              {t('autumnBreak.publishDate')}
            </p>
          </div>
        </div>

        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-1 observe-me slide-left">
          <Image
            src="/design-images/uploads/1788876190988-1kmpij-c1_06.jpg"
            alt={t('autumnBreak.imageAlt')}
            fill
            className="object-cover"
            style={{ objectPosition: 'center' }}
          />
        </div>
      </section>

      {/* Myrmidone Certification Section - Asymmetric Layout (Content Left / Image Right) */}
      <section className="grid lg:grid-cols-5 min-h-[80vh] design-section-gap rounded-3xl overflow-hidden">
        {/* Content Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#faf5f5] via-[#fef7f0] to-[#faf5f5] order-1">
          <div className="max-w-lg observe-me slide-right">
            <div className="inline-block px-6 py-2 bg-[#801515] text-white text-sm font-semibold rounded-full mb-6 uppercase tracking-wider">
              {t('myrmidoneCertification.badge')}
            </div>

            <h2 className="font-poppins text-2xl sm:text-4xl md:text-5xl xl:text-6xl font-bold mb-4 leading-[1.1]">
              <span className="text-[#801515]">{t('myrmidoneCertification.titleAccent')}</span>
              <br />
              <span className="text-gray-800 text-2xl md:text-3xl xl:text-4xl font-normal mt-3 block">
                {t('myrmidoneCertification.titleSub')}
              </span>
            </h2>

            <div className="w-16 h-1 bg-[#801515] mb-6" />

            <p className="text-lg text-gray-700 leading-relaxed mb-6 font-light italic">
              {t('myrmidoneCertification.description')}
            </p>

            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-6">
              {t('myrmidoneCertification.footnote')}
            </p>

            <p className="text-xs text-gray-400 mt-6 text-center lg:text-left">
              {t('myrmidoneCertification.publishDate')}
            </p>
          </div>
        </div>

        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 observe-me slide-left">
          <Image
            src="/design-images/uploads/1790167322524-f10xbz-whatsapp-image-2026-09-23-at-15.31.14.jpg"
            alt={t('myrmidoneCertification.imageAlt')}
            fill
            className="object-cover"
            style={{ objectPosition: 'center' }}
          />
        </div>
      </section>

      {/* Gift Vouchers Section - Asymmetric Layout (Image Left / Content Right) */}
      <section className="grid lg:grid-cols-5 min-h-[80vh] design-section-gap rounded-3xl overflow-hidden">
        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-1 observe-me slide-left">
          <Image
            src="/design-images/uploads/1777555438216-0082ds1_9891-enhanced-nr.jpg"
            alt={t('giftVouchers.imageAlt')}
            fill
            className="object-cover"
            style={{ objectPosition: 'center' }}
          />
        </div>

        {/* Content Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#faf5f5] via-[#fef7f0] to-[#faf5f5] order-2">
          <div className="max-w-lg observe-me slide-right">
            <div className="inline-block px-6 py-2 bg-[#801515] text-white text-sm font-semibold rounded-full mb-6 uppercase tracking-wider">
              {t('giftVouchers.badge')}
            </div>

            <h2 className="font-poppins text-2xl sm:text-4xl md:text-5xl xl:text-6xl font-bold mb-4 leading-[1.1]">
              <span className="text-[#801515]">{t('giftVouchers.titleAccent')}</span>
              <br />
              <span className="text-gray-800 text-2xl md:text-3xl xl:text-4xl font-normal mt-3 block">
                {t('giftVouchers.titleSub')}
              </span>
            </h2>

            <div className="w-16 h-1 bg-[#801515] mb-6" />

            <p className="text-lg text-gray-700 leading-relaxed mb-6 font-light italic">
              {t('giftVouchers.description')}
            </p>

            <div>
              <Link
                href="/gift-vouchers"
                className="group w-full lg:w-auto px-[26px] py-5 bg-[#801515] hover:bg-[#601010] text-white font-serif text-xl rounded-full transition-all duration-500 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl hover:shadow-[#801515]/30"
              >
                {t('giftVouchers.cta')}
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center lg:text-left">
              {t('giftVouchers.footnote')}
            </p>

            <p className="text-xs text-gray-400 mt-6 text-center lg:text-left">
              {t('giftVouchers.publishDate')}
            </p>
          </div>
        </div>
      </section>

      {/* TopHotel Awards Section - Image Right Layout with Oval Badge */}
      <section className="grid lg:grid-cols-5 min-h-[80vh] design-section-gap rounded-3xl overflow-hidden">
        {/* Content Side - 2 columns */}
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#faf5f5] via-[#fef7f0] to-[#faf5f5] order-2 lg:order-1">
          <div className="max-w-lg observe-me slide-right">
            <div className="inline-block px-6 py-2 bg-[#801515] text-white text-sm font-semibold rounded-full mb-6 uppercase tracking-wider">
              {t('topHotelAwards.badge')}
            </div>

            <h2 className="font-poppins text-2xl sm:text-4xl md:text-5xl xl:text-6xl font-bold mb-4 leading-[1.1]">
              <span className="text-[#801515]">{t('topHotelAwards.title')}</span>
              <br />
              <span className="text-gray-800 text-2xl md:text-3xl xl:text-4xl font-normal mt-3 block">
                {t('topHotelAwards.subtitle')}
              </span>
            </h2>

            <div className="w-16 h-1 bg-[#801515] mb-6" />

            <p className="text-lg text-gray-700 leading-relaxed mb-6 font-light italic">
              {t('topHotelAwards.description')}
            </p>

            <p className="text-xs text-gray-400">
              {t('topHotelAwards.publishDate')}
            </p>
          </div>
        </div>

        {/* Image Side - 3 columns */}
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-1 lg:order-2 observe-me slide-right">
          <Image
            src="/design-images/uploads/1781250193394-3i85fb-copy-of-last-week-tophotel-awards-nominee.png"
            alt={t('topHotelAwards.imageAlt')}
            fill
            className="object-cover object-bottom"
          />
        </div>
      </section>

      {/* What Awaits You Section */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white via-[#faf8f6] to-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16 observe-me slide-up">
            <h2 className="font-poppins text-3xl sm:text-5xl md:text-6xl font-bold mb-6 leading-[1.1]">
              <span className="text-[#801515]">{t('included.heading')}</span>
            </h2>
            <div className="w-16 h-1 bg-[#801515] mx-auto mb-8" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
              {t('included.subheading')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link
              href="/rooms"
              className="bg-white rounded-2xl shadow-lg p-8 text-center observe-me slide-up hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer"
              style={{ animationDelay: '0s' }}
            >
              <div className="flex justify-center mb-6">
                <Crown className="w-16 h-16 text-[#801515]" strokeWidth={1.5} />
              </div>
              <h3 className="font-poppins text-xl font-bold text-gray-900 mb-3">{t('included.item1Title')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('included.item1Desc')}</p>
            </Link>

            <Link
              href="/dining"
              className="bg-white rounded-2xl shadow-lg p-8 text-center observe-me slide-up hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer"
              style={{ animationDelay: '0.15s' }}
            >
              <div className="flex justify-center mb-6">
                <Wine className="w-16 h-16 text-[#801515]" strokeWidth={1.5} />
              </div>
              <h3 className="font-poppins text-xl font-bold text-gray-900 mb-3">{t('included.item2Title')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('included.item2Desc')}</p>
            </Link>

            <Link
              href="/spa"
              className="bg-white rounded-2xl shadow-lg p-8 text-center observe-me slide-up hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer"
              style={{ animationDelay: '0.3s' }}
            >
              <div className="flex justify-center mb-6">
                <Flower className="w-16 h-16 text-[#801515]" strokeWidth={1.5} />
              </div>
              <h3 className="font-poppins text-xl font-bold text-gray-900 mb-3">{t('included.item3Title')}</h3>
              <p className="text-gray-600 leading-relaxed">{t('included.item3Desc')}</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center py-24 px-6 bg-gradient-to-b from-[#faf8f6] to-white">
        <div className="max-w-3xl mx-auto text-center observe-me slide-up">
          <h2 className="font-poppins text-3xl sm:text-5xl md:text-6xl font-bold mb-8 leading-[1.1]">
            <span className="text-[#801515]">{t('cta.heading')}</span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed mb-12">
            {t('cta.body')}
          </p>
          <Link
            href="/rooms"
            className="group px-[26px] py-6 bg-[#801515] hover:bg-[#601010] text-white font-serif text-2xl rounded-full transition-all duration-500 inline-flex items-center gap-4 shadow-xl hover:shadow-2xl hover:shadow-[#801515]/40 hover:scale-105"
          >
            {t('cta.button')}
            <ArrowRight className="w-7 h-7 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQAccordion />

      <Footer />
    </main>
  );
}
